import { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Card, CardBody, Tabs, Tab, Select, SelectItem, Checkbox } from '@nextui-org/react';
import { startOfISOWeek, getISOWeek, format } from 'date-fns';
import type { WholesaleRecord, RetailRecord, MovementRecord, DataSource, UnitMode } from './types';
import {
  parseUSDate,
  shortMarket,
  wholesalePriceInUnit,
  retailPriceInUnit,
  MARKET_COLORS,
  REGION_COLORS,
} from './utils';
import { EmptyState } from '../../components/ui';

// Granularity of the evolution chart: raw daily points, or aggregated to
// ISO-week / calendar-month buckets (defaults to monthly — daily is noisy).
type Granularity = 'day' | 'week' | 'month';

const GRANULARITY_OPTIONS: { key: Granularity; label: string }[] = [
  { key: 'month', label: 'Mensual' },
  { key: 'week', label: 'Semanal' },
  { key: 'day', label: 'Diaria' },
];

// Canonical, lexically-sortable bucket key for a date.
const periodKeyFromDate = (d: Date, g: Granularity): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  if (g === 'month') return `${y}-${m}`;
  if (g === 'week') {
    const mon = startOfISOWeek(d);
    return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(mon.getDate()).padStart(2, '0')}`;
  }
  return `${y}-${m}-${String(d.getDate()).padStart(2, '0')}`;
};

// Same, from a USDA report date string (MM/DD/YYYY).
const periodKey = (reportDate: string, g: Granularity): string =>
  periodKeyFromDate(parseUSDate(reportDate), g);

// Human label for a bucket key on the x-axis.
const periodLabel = (key: string, g: Granularity): string => {
  const [y, m, d] = key.split('-').map(Number);
  if (g === 'month') return format(new Date(y, m - 1, 1), 'MMM yyyy');
  const dt = new Date(y, m - 1, d);
  if (g === 'week') return `S${getISOWeek(dt)} ${format(dt, 'MMM')}`;
  return format(dt, 'd MMM');
};
import useUserStore from '../../../../common/src/stores/useUserStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { SEASONS } from '../../../../common/src/utils/definitions';

interface EvolutionTabProps {
  wholesaleData: WholesaleRecord[];
  retailData: RetailRecord[];
  dataSource: DataSource;
  unit: UnitMode;
  commodity: string;
  /** "VS temporada": overlay the same window of a previous season. */
  vsSeason?: string | null;
  onVsSeasonChange?: (season: string | null) => void;
  comparisonData?: WholesaleRecord[];
  comparisonYearDiff?: number;
  loadingComparison?: boolean;
  /** Start year of the season the current date range belongs to — only
   *  EARLIER seasons make sense as comparison options. */
  currentSeasonStartYear?: number;
  /** Industry shipment volume (movement reports) for the volume bars.
   *  Lazy: the parent only fetches movement while showVolume is on. */
  showVolume?: boolean;
  onShowVolumeChange?: (on: boolean) => void;
  movementData?: MovementRecord[];
  loadingMovement?: boolean;
}

type EvolutionSubTab = 'general' | 'market' | 'variety' | 'origin';

// Fallback palette for series without an explicit color mapping
// (varieties/origins are open-ended, unlike markets/regions).
const FALLBACK_COLORS = [
  '#e11d48',
  '#2563eb',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#ca8a04',
  '#db2777',
  '#65a30d',
  '#4f46e5',
  '#14b8a6',
  '#f97316',
  '#8b5cf6',
  '#0ea5e9',
  '#84cc16',
  '#f59e0b',
];

const EvolutionTab = ({
  wholesaleData,
  retailData,
  dataSource,
  unit,
  commodity,
  vsSeason = null,
  onVsSeasonChange,
  comparisonData = [],
  comparisonYearDiff = 0,
  loadingComparison = false,
  currentSeasonStartYear = 0,
  showVolume = false,
  onShowVolumeChange,
  movementData = [],
  loadingMovement = false,
}: EvolutionTabProps) => {
  const { darkMode } = useUserStore();
  const { t } = useTranslation('usda-market');
  const [subTab, setSubTab] = useState<EvolutionSubTab>('general');
  const [granularity, setGranularity] = useState<Granularity>('month');
  const unitLabel = unit === 'kg' ? '$/kg' : '$/lb';
  const foreColor = darkMode ? '#A1A1AA' : '#71717A';
  // Volume axis: tons when prices are $/kg, thousands of lb when $/lb.
  const volumeLabel = unit === 'kg' ? t('Volumen (ton)') : t('Volumen (miles lb)');
  const lbToVolumeUnit = (lb: number) =>
    unit === 'kg' ? (lb * 0.453592) / 1000 : lb / 1000;

  const buildLineChart = (
    groupDatePrices: Record<string, Record<string, number[]>>,
    allDates: string[],
    title: string,
    colorMap: Record<string, string>,
    comparisonSuffix = '',
    volume?: { totals: Record<string, number>; label: string }
  ): { options: ApexOptions; series: ApexAxisChartSeries } => {
    const series = Object.entries(groupDatePrices).map(([name, datePrices]) => ({
      name,
      data: allDates.map((d) => {
        const ps = datePrices[d];
        return ps ? parseFloat((ps.reduce((a, b) => a + b, 0) / ps.length).toFixed(2)) : null;
      }) as (number | null)[],
    }));

    // "Los Angeles (2024-2025)" reuses Los Angeles' color, dashed.
    const baseName = (n: string) =>
      comparisonSuffix && n.endsWith(comparisonSuffix)
        ? n.slice(0, -comparisonSuffix.length)
        : n;
    const uniqueBases = Array.from(
      new Set(series.map((s) => baseName(s.name || '')))
    );

    const priceColors = series.map((s) => {
      const b = baseName(s.name || '');
      return (
        colorMap[b] ||
        FALLBACK_COLORS[uniqueBases.indexOf(b) % FALLBACK_COLORS.length]
      );
    });

    // Mixed chart: price lines + industry volume bars on a secondary axis.
    const hasVolume = volume != null;
    const priceSeriesName = series[0]?.name || '';
    const finalSeries: ApexAxisChartSeries = [
      ...series.map((s) => ({ ...s, type: 'line' as const })),
      ...(hasVolume
        ? [
            {
              name: volume!.label,
              type: 'column' as const,
              data: allDates.map((d) => volume!.totals[d] ?? null),
            },
          ]
        : []),
    ];
    const fmtVol = (v: number) =>
      v == null ? '' : Math.round(v).toLocaleString('es');

    return {
      series: finalSeries,
      options: {
        chart: { type: 'line', height: 420, foreColor, toolbar: { show: true }, zoom: { enabled: true } },
        stroke: {
          curve: 'smooth',
          width: [
            ...series.map(() => 2.5),
            ...(hasVolume ? [0] : []),
          ],
          dashArray: [
            ...series.map((s) =>
              comparisonSuffix && (s.name || '').endsWith(comparisonSuffix) ? 6 : 0
            ),
            ...(hasVolume ? [0] : []),
          ],
        },
        colors: [...priceColors, ...(hasVolume ? ['#94a3b8'] : [])],
        fill: {
          opacity: [...series.map(() => 1), ...(hasVolume ? [0.35] : [])],
        },
        plotOptions: { bar: { columnWidth: '55%' } },
        xaxis: {
          categories: allDates.map((k) => periodLabel(k, granularity)),
          labels: { rotate: -45, style: { fontSize: '10px' } },
        },
        yaxis: hasVolume
          ? [
              // All price series share one axis (same seriesName groups them)
              ...series.map((s, i) => ({
                seriesName: priceSeriesName,
                show: i === 0,
                title: { text: unitLabel },
                labels: { formatter: (v: number) => `$${v?.toFixed(2) ?? ''}` },
              })),
              {
                seriesName: volume!.label,
                opposite: true,
                min: 0,
                title: { text: volume!.label },
                labels: { formatter: fmtVol },
              },
            ]
          : {
              title: { text: unitLabel },
              labels: { formatter: (v: number) => `$${v?.toFixed(2) ?? ''}` },
            },
        tooltip: {
          y: {
            formatter: (v: number, opts?: { seriesIndex?: number }) => {
              if (v == null) return 'N/A';
              const isVol =
                hasVolume && opts?.seriesIndex === finalSeries.length - 1;
              return isVol ? fmtVol(v) : `$${v.toFixed(2)}`;
            },
          },
        },
        legend: { position: 'top' },
        title: { text: title, style: { fontSize: '15px', fontWeight: 600 } },
      },
    };
  };

  // Wholesale / FOB evolution
  const wholesaleCharts = useMemo(() => {
    if (dataSource === 'retail') return null;
    const data = wholesaleData;
    if (data.length === 0) return null;

    const allDates = Array.from(
      new Set(data.map((r) => periodKey(r.report_date, granularity)))
    ).sort();
    const sourceLabel = dataSource === 'wholesale' ? 'Wholesale' : 'FOB';

    // Filas que no se pueden convertir a precio por peso: su formato de
    // empaque no declara peso y tampoco está en la tabla de estándares.
    // Antes se descartaban en silencio y el gráfico salía vacío sin decir
    // por qué (palta era el caso extremo: 100% de las filas).
    // By market
    const byMarket: Record<string, Record<string, number[]>> = {};
    data.forEach((r) => {
      const m = shortMarket(r.market_location_name || '');
      const p = wholesalePriceInUnit(r, unit);
      if (p == null) return;
      const key = periodKey(r.report_date, granularity);
      if (!byMarket[m]) byMarket[m] = {};
      if (!byMarket[m][key]) byMarket[m][key] = [];
      byMarket[m][key].push(p);
    });

    // General: una sola serie con todo agregado. Responde "¿el mercado sube o
    // baja?" sin la madeja de 8 mercados u 11 orígenes, y hace legible la
    // comparación entre temporadas (2 líneas en vez de 16).
    const byGeneral: Record<string, Record<string, number[]>> = {};
    const generalLabel = t('Todos');
    data.forEach((r) => {
      const p = wholesalePriceInUnit(r, unit);
      if (p == null) return;
      const key = periodKey(r.report_date, granularity);
      if (!byGeneral[generalLabel]) byGeneral[generalLabel] = {};
      if (!byGeneral[generalLabel][key]) byGeneral[generalLabel][key] = [];
      byGeneral[generalLabel][key].push(p);
    });

    // By variety
    const byVariety: Record<string, Record<string, number[]>> = {};
    data.forEach((r) => {
      if (!r.variety || r.variety === 'N/A') return;
      const p = wholesalePriceInUnit(r, unit);
      if (p == null) return;
      const key = periodKey(r.report_date, granularity);
      if (!byVariety[r.variety]) byVariety[r.variety] = {};
      if (!byVariety[r.variety][key]) byVariety[r.variety][key] = [];
      byVariety[r.variety][key].push(p);
    });

    // By origin
    const byOrigin: Record<string, Record<string, number[]>> = {};
    data.forEach((r) => {
      if (!r.origin || r.origin === 'N/A') return;
      const p = wholesalePriceInUnit(r, unit);
      if (p == null) return;
      const key = periodKey(r.report_date, granularity);
      if (!byOrigin[r.origin]) byOrigin[r.origin] = {};
      if (!byOrigin[r.origin][key]) byOrigin[r.origin][key] = [];
      byOrigin[r.origin][key].push(p);
    });

    // VS temporada: overlay the comparison season as dashed series. Dates
    // are shifted forward by the season gap so they land on the current
    // axis (e.g. Nov 2024 → Nov 2025 bucket when comparing 2024-2025).
    const comparisonSuffix = vsSeason ? ` (${vsSeason})` : '';
    if (vsSeason && comparisonYearDiff !== 0 && comparisonData.length > 0) {
      const shiftedKey = (reportDate: string) => {
        const d = parseUSDate(reportDate);
        return periodKeyFromDate(
          new Date(d.getFullYear() + comparisonYearDiff, d.getMonth(), d.getDate()),
          granularity
        );
      };
      const push = (
        bucket: Record<string, Record<string, number[]>>,
        name: string,
        key: string,
        p: number
      ) => {
        if (!bucket[name]) bucket[name] = {};
        if (!bucket[name][key]) bucket[name][key] = [];
        bucket[name][key].push(p);
      };
      comparisonData.forEach((r) => {
        const p = wholesalePriceInUnit(r, unit);
        if (p == null) return;
        const key = shiftedKey(r.report_date);
        if (!allDates.includes(key)) return; // outside the current axis
        const m = shortMarket(r.market_location_name || '');
        push(byGeneral, `${generalLabel}${comparisonSuffix}`, key, p);
        push(byMarket, `${m}${comparisonSuffix}`, key, p);
        if (r.variety && r.variety !== 'N/A')
          push(byVariety, `${r.variety}${comparisonSuffix}`, key, p);
        if (r.origin && r.origin !== 'N/A')
          push(byOrigin, `${r.origin}${comparisonSuffix}`, key, p);
      });
    }

    // Industry shipment volume (sum of "1 lb units" across districts),
    // bucketed onto the same axis. Shown as bars on a secondary axis.
    let volume: { totals: Record<string, number>; label: string } | undefined;
    if (showVolume && movementData.length > 0) {
      const totals: Record<string, number> = {};
      const axis = new Set(allDates);
      movementData.forEach((r) => {
        const lb = r['1 lb units'];
        if (lb == null) return;
        const key = periodKey(r.report_begin_date, granularity);
        if (!axis.has(key)) return;
        totals[key] = (totals[key] || 0) + lbToVolumeUnit(Number(lb));
      });
      if (Object.keys(totals).length > 0) {
        volume = { totals, label: volumeLabel };
      }
    }

    return {
      allDates,
      general: buildLineChart(byGeneral, allDates, `${commodity} ${sourceLabel} — ${unitLabel}`, { [generalLabel]: '#BE123C' }, comparisonSuffix, volume),
      market: buildLineChart(byMarket, allDates, `${commodity} ${sourceLabel} — ${unitLabel} by Market`, MARKET_COLORS, comparisonSuffix, volume),
      variety: buildLineChart(byVariety, allDates, `${commodity} ${sourceLabel} — ${unitLabel} by Variety`, {}, comparisonSuffix, volume),
      origin: buildLineChart(byOrigin, allDates, `${commodity} ${sourceLabel} — ${unitLabel} by Origin`, {}, comparisonSuffix, volume),
    };
  }, [wholesaleData, dataSource, unit, commodity, granularity, vsSeason, comparisonData, comparisonYearDiff, showVolume, movementData]);

  // Retail evolution
  const retailCharts = useMemo(() => {
    if (dataSource !== 'retail') return null;
    if (retailData.length === 0) return null;

    const allDates = Array.from(
      new Set(retailData.map((r) => periodKey(r.report_begin_date, granularity)))
    ).sort();

    // By region
    const byRegion: Record<string, Record<string, number[]>> = {};
    retailData.forEach((r) => {
      if (!r.region) return;
      const p = retailPriceInUnit(r, unit, commodity);
      if (p == null) return;
      const key = periodKey(r.report_begin_date, granularity);
      if (!byRegion[r.region]) byRegion[r.region] = {};
      if (!byRegion[r.region][key]) byRegion[r.region][key] = [];
      byRegion[r.region][key].push(p);
    });

    // General: todo agregado en una línea (mismo criterio que wholesale)
    const byGeneral: Record<string, Record<string, number[]>> = {};
    const generalLabel = t('Todos');
    retailData.forEach((r) => {
      const p = retailPriceInUnit(r, unit, commodity);
      if (p == null) return;
      const key = periodKey(r.report_begin_date, granularity);
      if (!byGeneral[generalLabel]) byGeneral[generalLabel] = {};
      if (!byGeneral[generalLabel][key]) byGeneral[generalLabel][key] = [];
      byGeneral[generalLabel][key].push(p);
    });

    // By variety
    const byVariety: Record<string, Record<string, number[]>> = {};
    retailData.forEach((r) => {
      if (!r.variety || r.variety === 'N/A') return;
      const p = retailPriceInUnit(r, unit, commodity);
      if (p == null) return;
      const key = periodKey(r.report_begin_date, granularity);
      if (!byVariety[r.variety]) byVariety[r.variety] = {};
      if (!byVariety[r.variety][key]) byVariety[r.variety][key] = [];
      byVariety[r.variety][key].push(p);
    });

    return {
      allDates,
      general: buildLineChart(byGeneral, allDates, `${commodity} Retail — ${unitLabel}`, { [generalLabel]: '#BE123C' }),
      market: buildLineChart(byRegion, allDates, `${commodity} Retail — ${unitLabel} by Region`, REGION_COLORS),
      variety: buildLineChart(byVariety, allDates, `${commodity} Retail — ${unitLabel} by Variety`, {}),
      origin: null,
    };
  }, [retailData, dataSource, unit, commodity, granularity]);

  const charts = dataSource === 'retail' ? retailCharts : wholesaleCharts;

  if (!charts) {
    return (
      <div className='mt-4'>
        <EmptyState title={t('No hay datos disponibles')} />
      </div>
    );
  }

  const activeChart =
    subTab === 'general' ? charts.general :
    subTab === 'market' ? charts.market :
    subTab === 'variety' ? charts.variety :
    charts.origin;


  return (
    <div className='pt-4 space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <Tabs
          selectedKey={subTab}
          onSelectionChange={(key) => setSubTab(key as EvolutionSubTab)}
          size='sm'
          variant='bordered'
        >
          <Tab key='market' title={dataSource === 'retail' ? t('Por Región') : t('Por Mercado')} />
          <Tab key='variety' title={t('Por Variedad')} />
          {dataSource !== 'retail' && <Tab key='origin' title={t('Por Origen')} />}
          <Tab key='general' title={t('Tendencia')} />
        </Tabs>

        <div className='flex items-end gap-3'>
          {dataSource !== 'retail' && (
            <Checkbox
              size='sm'
              isSelected={showVolume}
              onValueChange={(v) => onShowVolumeChange?.(v)}
              className='pb-2'
            >
              {t('Mostrar volumen')}
              {loadingMovement && showVolume ? '…' : ''}
            </Checkbox>
          )}

          {dataSource !== 'retail' && onVsSeasonChange && (
            <Select
              size='sm'
              label={t('VS temporada')}
              placeholder={t('Sin comparación')}
              className='w-44'
              isLoading={loadingComparison}
              selectedKeys={vsSeason ? new Set([vsSeason]) : new Set([])}
              onSelectionChange={(keys) => {
                const k = Array.from(keys)[0] as string | undefined;
                onVsSeasonChange(!k || k === 'none' ? null : k);
              }}
            >
              {[
                <SelectItem key='none' value='none'>
                  {t('Sin comparación')}
                </SelectItem>,
                // Any season except the one already on screen
                ...Object.values(SEASONS)
                  .map((s) => s.name)
                  .filter(
                    (name) =>
                      !currentSeasonStartYear ||
                      parseInt(name, 10) !== currentSeasonStartYear
                  )
                  .reverse()
                  .map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  )),
              ]}
            </Select>
          )}

          <Select
            size='sm'
            label={t('Tipo de visualización')}
            className='w-40'
            selectedKeys={new Set([granularity])}
            onSelectionChange={(keys) => {
              const k = Array.from(keys)[0];
              if (k) setGranularity(k as Granularity);
            }}
          >
            {GRANULARITY_OPTIONS.map((o) => (
              <SelectItem key={o.key} value={o.key}>
                {t(o.label)}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>

      {activeChart && (
        <Card>
          <CardBody>
            <ReactApexChart
              options={activeChart.options}
              series={activeChart.series}
              type='line'
              height={420}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default EvolutionTab;
